import React, { useState } from 'react';
import { Search, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { GlassButton } from '../ui';
import { inputStyle, labelStyle, fieldWrapStyle } from './seoPanelStyles';
import { ImportedPage, HeadMetaEntry, updateSitePage, saveSiteHeadMeta } from '../../services/api';

interface Props {
  siteId: string;
  /** Bundle-relative path of the page currently open in the editor (e.g. "about.html"). */
  pageRel: string;
  /** Non-null when pageRel is a page AIME synthesised (chrome-donor page); null for a real bundle
   *  host file — this is what decides which endpoint Save SEO writes to. */
  generatedPage: ImportedPage | null;
  /** The site's FULL head_meta map (bundle host pages only) — this panel edits exactly one key
   *  of it and PUTs the whole map back (whole-map replace, per the API contract). */
  headMeta: Record<string, HeadMetaEntry>;
  /** True when the parent's `getSiteHeadMeta` load failed. `headMeta` is then an INCOMPLETE stand-in
   *  ({}), not a confirmed-empty map — a whole-map-replace Save built on it would wipe every other
   *  bundle host page's stored SEO metadata. Disables Save for host pages (code review I12);
   *  generated pages are unaffected since they write their own single-page endpoint. */
  headMetaFailed: boolean;
  /** True when pageRel is the bundle's entrypoint — ticking noindex here hides the whole site. */
  isEntrypoint: boolean;
  onGeneratedPageSaved: (page: ImportedPage) => void;
  onHeadMetaSaved: (headMeta: Record<string, HeadMetaEntry>) => void;
}

const blankEntry: HeadMetaEntry = {
  meta_title: '', meta_description: '', og_image: '', canonical_url: '', noindex: false,
};

const seedFrom = (generatedPage: ImportedPage | null, headMeta: Record<string, HeadMetaEntry>, pageRel: string): HeadMetaEntry =>
  generatedPage
    ? {
        meta_title: generatedPage.meta_title || '',
        meta_description: generatedPage.meta_description || '',
        og_image: generatedPage.og_image || '',
        canonical_url: generatedPage.canonical_url || '',
        noindex: !!generatedPage.noindex,
      }
    : { ...blankEntry, ...(headMeta[pageRel] || {}) };

/**
 * Per-page SEO editor for imported-static sites — the metadata-editing UI those sites had none
 * of before this feature. Mirrors SEOPanel.tsx (component sites) but targets one of two
 * different persistence endpoints depending on the page kind:
 *   - a GENERATED page (synthesised from a chrome donor) -> PUT /generated-pages/{page_id}
 *   - a BUNDLE HOST page (a real .html file on disk)      -> PUT /head-meta (whole-map replace)
 *
 * Save is a deliberate separate explicit action — NOT folded into the editor's top-bar Save
 * (a whole-array DOM-patches replace guarded by seedFailed) and NOT on the section autosave, so a
 * partial failure can never be ambiguous about which resource persisted.
 *
 * Render this with `key={pageRel}` from the parent so navigating to a different page always
 * remounts fresh state rather than carrying over unsaved edits from the previous page.
 */
const ImportedPageSEOPanel: React.FC<Props> = ({
  siteId, pageRel, generatedPage, headMeta, headMetaFailed, isEntrypoint, onGeneratedPageSaved, onHeadMetaSaved,
}) => {
  // Only bundle host pages (generatedPage === null) go through the whole-map-replace /head-meta
  // endpoint — a generated page always writes its own single-page endpoint regardless of whether
  // the head_meta map loaded, so it is never affected by headMetaFailed.
  const saveBlockedByFailedLoad = !generatedPage && headMetaFailed;
  const seed = seedFrom(generatedPage, headMeta, pageRel);
  const [metaTitle, setMetaTitle] = useState(seed.meta_title);
  const [metaDescription, setMetaDescription] = useState(seed.meta_description);
  const [ogImage, setOgImage] = useState(seed.og_image);
  const [canonicalUrl, setCanonicalUrl] = useState(seed.canonical_url);
  const [noindex, setNoindex] = useState(seed.noindex);
  const [collapsed, setCollapsed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  const revert = () => {
    setMetaTitle(seed.meta_title);
    setMetaDescription(seed.meta_description);
    setOgImage(seed.og_image);
    setCanonicalUrl(seed.canonical_url);
    setNoindex(seed.noindex);
    setStatus('');
  };

  const save = async () => {
    if (saveBlockedByFailedLoad) {
      // Belt-and-suspenders: the button is disabled for this case too, but never let a stale
      // click PUT a whole-map replace built on an incomplete {} stand-in map.
      setStatus('Save failed: could not load existing SEO metadata for other pages — saving is disabled to avoid overwriting them. Reload the page and try again.');
      return;
    }
    setSaving(true);
    setStatus('');
    try {
      if (generatedPage) {
        const updated = await updateSitePage(siteId, generatedPage.page_id, {
          meta_title: metaTitle,
          meta_description: metaDescription,
          og_image: ogImage,
          canonical_url: canonicalUrl,
          noindex,
        });
        onGeneratedPageSaved(updated);
      } else {
        const nextMap: Record<string, HeadMetaEntry> = {
          ...headMeta,
          [pageRel]: {
            meta_title: metaTitle,
            meta_description: metaDescription,
            og_image: ogImage,
            canonical_url: canonicalUrl,
            noindex,
          },
        };
        const res = await saveSiteHeadMeta(siteId, nextMap);
        onHeadMetaSaved(res.head_meta);
      }
      setStatus('Saved.');
    } catch (e: any) {
      setStatus('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ borderTop: '1px solid var(--stroke, rgba(15,23,42,0.10))' }}>
      <button
        onClick={() => setCollapsed(c => !c)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
          background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)',
          fontSize: '0.8125rem', fontWeight: 600, textAlign: 'left',
        }}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        <Search size={14} style={{ color: 'var(--text-secondary)' }} />
        <span>SEO Settings</span>
      </button>

      {!collapsed && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Meta Title</label>
            <input
              type="text"
              value={metaTitle}
              onChange={e => setMetaTitle(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Meta Description</label>
            <textarea
              value={metaDescription}
              rows={3}
              onChange={e => setMetaDescription(e.target.value)}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 70, lineHeight: 1.5, fontFamily: 'inherit' }}
            />
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Social Share Image (OG)</label>
            <input
              type="url"
              value={ogImage}
              placeholder="https://example.com/og-image.jpg"
              onChange={e => setOgImage(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Canonical URL</label>
            <input
              type="url"
              value={canonicalUrl}
              placeholder="https://yourdomain.com/page"
              onChange={e => setCanonicalUrl(e.target.value)}
              style={inputStyle}
            />
          </div>

          <div style={fieldWrapStyle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--text)' }}>
              <input type="checkbox" checked={noindex} onChange={e => setNoindex(e.target.checked)} />
              Hide from search engines (noindex)
            </label>
            {noindex && isEntrypoint && (
              <p style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.7rem', color: 'var(--danger, #dc2626)', marginTop: 4 }}>
                <AlertTriangle size={12} /> This is the site's home page — noindex hides the whole site from search.
              </p>
            )}
            {noindex && !isEntrypoint && (
              <p style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 3 }}>
                This page will be excluded from the sitemap and marked noindex, nofollow. Removal from search results can take days.
              </p>
            )}
          </div>

          {saveBlockedByFailedLoad && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 4,
              fontSize: '0.75rem', color: 'var(--danger, #dc2626)', marginBottom: 10,
            }}>
              <AlertTriangle size={12} style={{ marginTop: 2, flexShrink: 0 }} />
              <span>Couldn't load existing SEO metadata for this site's other pages, so saving here is disabled — it would otherwise overwrite them. Reload the page to try again.</span>
            </div>
          )}

          {status && (
            <div style={{
              fontSize: '0.75rem',
              color: status.startsWith('Save failed') ? 'var(--danger, #dc2626)' : 'var(--text-secondary)',
              marginBottom: 10,
            }}>
              {status}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <GlassButton variant="primary" onClick={save} disabled={saving || saveBlockedByFailedLoad}>
              {saving ? 'Saving…' : 'Save SEO'}
            </GlassButton>
            <GlassButton variant="secondary" onClick={revert} disabled={saving}>
              Revert
            </GlassButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportedPageSEOPanel;
